import {
    MouseEvent, useCallback, useEffect, useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { Column } from 'react-rainbow-components';
import useCustomers from 'data/firestore/agent/customer/useCollectionOnce';
import { orderBy, query, where } from 'firebase/firestore';
import { EntityGet } from 'data/firestore/types';
import { Customer } from 'data/firestore/agent/customer/types';
import Pagination from 'components/Pagination';
import Table from 'components/Table';
import useCustomerPanel from 'hooks/useCustomerPanel';
import { PAGE_SIZE } from '../../../../constants';
import UserColumn from '../../../Customers/CustomersList/columns/user';
import ActionsColumn from './columns/actions';
import { Container, EntriesText, PaginationContainer } from '../../WorkplacesList/styled';

interface CustomersListProps {
    search?: string;
}

const CustomersList = ({ search }: CustomersListProps) => {
    const { agentId = '', workplaceId = '' } = useParams();
    const [activePage, setActivePage] = useState(1);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
    const { openCustomerPanel } = useCustomerPanel();
    const {
        data: customers,
        isLoading: isLoadingCustomers,
        error,
    } = useCustomers(
        agentId,
        {
            disabled: !agentId || !workplaceId,
            listQueryFn: (ref) => query(
                ref,
                where('workplaceId', '==', workplaceId),
                orderBy('createdAt', sortDirection),
            ),
            track: [agentId, workplaceId, sortDirection],
        },
    );

    useEffect(() => setActivePage(1), [search]);

    const dataToShow = (
        customers?.slice(PAGE_SIZE * (activePage - 1), PAGE_SIZE * activePage) || []
    );
    const totalHits = customers.length || 0;
    const pages = Math.ceil(totalHits / PAGE_SIZE);
    const firstItemOfActivePage = dataToShow.length === 0 ? 0 : PAGE_SIZE * (activePage - 1) + 1;
    const lastItemOfActivePage = Math.min(
        PAGE_SIZE * activePage,
        (PAGE_SIZE * activePage - PAGE_SIZE) + dataToShow.length,
    );

    const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
        if (isLoadingCustomers) return;
        if (page === activePage) return;
        setActivePage(page);
    };

    const handleOnSort = useCallback(
        (
            event: MouseEvent<HTMLElement>,
            field: string,
            nextSortDirection: 'asc' | 'desc',
        ) => setSortDirection(nextSortDirection),
        [],
    );

    if (error || (!customers && !isLoadingCustomers)) {
        return (
            <Container>
                <h1>Something went wrong</h1>
                {error?.message && (
                    <p>
                        {error.name}
                        :
                        {' '}
                        {error.code}
                    </p>
                )}
            </Container>
        );
    }

    return (
        <Container>
            <Table
                data={dataToShow}
                keyField="id"
                isLoading={isLoadingCustomers}
                sortedBy="createdAt"
                onSort={handleOnSort}
                sortDirection={sortDirection}
            >
                <Column
                    header="Name"
                    field="id"
                    component={UserColumn}
                    disableLink
                    onClick={(customer: EntityGet<Customer>) => openCustomerPanel({
                        data: customer,
                    })}
                />
                <Column
                    header="Actions"
                    field="id"
                    agentId={agentId}
                    width={200}
                    component={ActionsColumn}
                />
            </Table>
            <PaginationContainer>
                <EntriesText>
                    Showing
                    {' '}
                    {firstItemOfActivePage}
                    {' '}
                    to
                    {' '}
                    {lastItemOfActivePage}
                    {' '}
                    of
                    {' '}
                    {totalHits}
                    {' '}
                    entries.
                </EntriesText>
                <Pagination
                    activePage={activePage}
                    pages={pages}
                    onChange={handlePageChange}
                />
            </PaginationContainer>
        </Container>
    );
};

export default CustomersList;
